// Admin Users Manager - Business logic for user statistics
// Handles user statistics, plan distributions, and user analytics

import { Manager } from '../../../../../core';
import { logger } from '../../../../../core';

export class AdminUsersManager extends Manager {
  constructor(initialSignupRange = {}) {
    super();
    this.signupRange = initialSignupRange || {};
    this.userStats = {
      totalUsers: 0,
      planDistribution: {},
      tierDistribution: {},
      planTierDistribution: {},
      roleDistribution: {},
      betaTesters: 0,
      recentSignups: 0,
      activeUsers: 0,
      signupsByDay: [],
      signupsByWeek: [],
      signupsByMonth: []
    };
    this.isLoading = false;
  }

  /**
   * Initialize global variables and setup
   */
  async initGlobalVariables() {
    // Initialize user statistics state
    this.userStats = {
      totalUsers: 0,
      planDistribution: {},
      tierDistribution: {},
      planTierDistribution: {},
      roleDistribution: {},
      betaTesters: 0,
      recentSignups: 0,
      activeUsers: 0,
      verifiedEmails: 0,
      supporters: 0,
      stripeCustomers: 0,
      activeSubscriptions: 0,
      productsPurchased: 0,
      totalStorageBytes: 0,
      paidUsersStorageBytes: 0,
      paidUsersCount: 0,
      creditHolds: 0,
      totalApps: 0,
      appsByPlanType: { free: 0, paid: 0 },
      averageAppsPerUser: 0,
      paidUsersWithApps: 0,
      totalAppsByPaidUsers: 0,
      signupsByDay: [],
      signupsByWeek: [],
      signupsByMonth: []
    };
    this.isLoading = false;
  }

  /**
   * Initialize systems and data fetching
   */
  async initSystems() {
    // Load user statistics when manager is initialized
    await this.loadUserStats(this.signupRange);
    this.isInitialized = true;

    logger.log(
      { tags: 'admin-users-manager', color1: 'blue' },
      'AdminUsersManager initialized successfully'
    );
  }

  /**
   * Load user statistics data
   */
  async loadUserStats(signupRange = {}) {
    try {
      this.isLoading = true;

      const qs = new URLSearchParams();
      const signupsStart = signupRange?.signupsStart;
      const signupsEnd = signupRange?.signupsEnd;
      const excludeMigrationDays = signupRange?.excludeMigrationDays;
      if (typeof signupsStart === 'string' && signupsStart.trim()) {
        qs.set('signupsStart', signupsStart);
      }
      if (typeof signupsEnd === 'string' && signupsEnd.trim()) {
        qs.set('signupsEnd', signupsEnd);
      }
      if (excludeMigrationDays) {
        qs.set('excludeMigrationDays', 'true');
      }

      const response = await fetch(`/api/admin/user-stats${qs.toString() ? `?${qs.toString()}` : ''}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user statistics');
      }

      const data = await response.json();

      if (data.success && data.stats) {
        this.userStats = data.stats;
      } else {
        throw new Error(data.error || 'Invalid response format');
      }

    } catch (error) {
      logger.error(
        { tags: 'admin-users-manager|error', color1: 'red' },
        'Failed to load user statistics:',
        error
      );

      // Set to non-numeric indicators to show data is unavailable
      this.userStats = {
        totalUsers: '—',
        planDistribution: {},
        tierDistribution: {},
        planTierDistribution: {},
        roleDistribution: {},
        betaTesters: '—',
        recentSignups: '—',
        activeUsers: '—',
        verifiedEmails: '—',
        supporters: '—',
        stripeCustomers: '—',
        activeSubscriptions: '—',
        productsPurchased: '—',
        totalStorageBytes: '—',
        paidUsersStorageBytes: '—',
        paidUsersCount: '—',
        creditHolds: '—',
        totalApps: '—',
        appsByPlanType: { free: '—', paid: '—' },
        averageAppsPerUser: '—',
        paidUsersWithApps: '—',
        totalAppsByPaidUsers: '—',
        freeUsersWithApps: '—',
        freeUsersStorageBytes: '—',
        signupsByDay: [],
        signupsByWeek: [],
        signupsByMonth: []
      };
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Get user statistics
   */
  getUserStats() {
    return this.userStats;
  }

  /**
   * Get signup series for the requested granularity.
   */
  getSignupSeries(granularity = 'day') {
    if (granularity === 'week') {
      return this.userStats.signupsByWeek || [];
    }

    if (granularity === 'month') {
      return this.userStats.signupsByMonth || [];
    }

    return this.userStats.signupsByDay || [];
  }

  /**
   * Refresh user statistics
   */
  async refreshStats(signupRange = null) {
    const nextRange = signupRange || this.signupRange || {};
    this.signupRange = nextRange;
    await this.loadUserStats(nextRange);
  }
}